// Document upload restrictions based on city regulations
export type DocumentType = 'document' | 'picture'

export const CITY_NAMES = {
  THUNDER_BAY: 'Thunder Bay',
} as const

export type CityName = (typeof CITY_NAMES)[keyof typeof CITY_NAMES]

export interface RestrictionContext {
  city?: string
  documentType: DocumentType
}

export interface FileRestriction {
  errorMessage: string
  extensions: string[]
  formats: string[]
}

interface CityRestrictionConfig {
  cityName: CityName
  restrictions: {
    [key in DocumentType]: FileRestriction
  }
}

const CITY_RESTRICTIONS: CityRestrictionConfig[] = [
  {
    cityName: CITY_NAMES.THUNDER_BAY,
    restrictions: {
      document: {
        errorMessage:
          'Due to regulatory requirements, only PDF files are accepted.',
        extensions: ['.pdf'],
        formats: ['application/pdf'],
      },
      picture: {
        errorMessage: 'Please upload a valid image file.',
        extensions: ['.jpg', '.jpeg', '.png'],
        formats: ['image/jpg', 'image/jpeg', 'image/png'],
      },
    },
  },
]

const DEFAULT_RESTRICTION: FileRestriction = {
  errorMessage: 'Please upload JPG, PNG, or PDF files only.',
  extensions: ['.jpg', '.jpeg', '.png', '.pdf'],
  formats: ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'],
}

export const getFileRestriction = (
  context: RestrictionContext
): FileRestriction => {
  const { city, documentType } = context

  // Case-insensitive comparison with trimmed whitespace for safety
  const normalizedCity = city?.trim().toLowerCase()
  const cityConfig = CITY_RESTRICTIONS.find(
    (config) => config.cityName.toLowerCase() === normalizedCity
  )

  if (cityConfig) {
    return cityConfig.restrictions[documentType]
  }

  // Default for pictures (all cities except those with specific restrictions)
  if (documentType === 'picture') {
    return {
      errorMessage: 'Please upload JPG or PNG images only.',
      extensions: ['.jpg', '.jpeg', '.png'],
      formats: ['image/jpg', 'image/jpeg', 'image/png'],
    }
  }

  return DEFAULT_RESTRICTION
}
